import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from './SEO';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { useAdRedirect, AdToast } from '../hooks/useAdRedirect';
import { AdUnit } from './AdUnit';
import { 
  Code, 
  Copy, 
  Check, 
  ArrowLeft, 
  RotateCcw, 
  Download, 
  Plus, 
  Trash2, 
  ShieldCheck, 
  Bot 
} from 'lucide-react';

export const RobotsTxtGenerator = () => {
  const { withAd, toast } = useAdRedirect();
  const [defaultAccess, setDefaultAccess] = useState('allow'); // 'allow' | 'disallow'
  const [crawlDelay, setCrawlDelay] = useState('0'); // 0 = none
  const [sitemapUrl, setSitemapUrl] = useState('https://toolmetrico.com/sitemap.xml');
  const [rules, setRules] = useState([
    { userAgent: '*', access: 'allow', path: '/' },
    { userAgent: 'Googlebot-Image', access: 'disallow', path: '/private-images/' },
    { userAgent: 'ChatGPT-User', access: 'disallow', path: '/admin/' }
  ]);
  const [copied, setCopied] = useState(false);

  const addRule = () => {
    setRules([...rules, { userAgent: '*', access: 'disallow', path: '/temp/' }]);
  };

  const removeRule = (index) => {
    setRules(rules.filter((_, i) => i !== index));
  };

  const updateRule = (index, field, value) => {
    const updated = [...rules];
    updated[index][field] = value;
    setRules(updated);
  };

  // Generate Robots.txt raw string
  const generateRobotsTxt = () => {
    let output = `# robots.txt generated by Toolmetrico SEO Suite\n# https://toolmetrico.com/robots-txt-generator\n\n`;
    
    rules.forEach(rule => {
      output += `User-agent: ${rule.userAgent}\n`;
      if (rule.access === 'allow') {
        output += `Allow: ${rule.path}\n`;
      } else {
        output += `Disallow: ${rule.path}\n`;
      }
      if (crawlDelay !== '0') {
        output += `Crawl-delay: ${crawlDelay}\n`;
      }
      output += `\n`;
    });

    if (sitemapUrl) {
      output += `Sitemap: ${sitemapUrl}\n`;
    }

    return output.trim();
  };

  const robotsOutput = generateRobotsTxt();

  const handleCopy = () => {
    navigator.clipboard.writeText(robotsOutput);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const element = document.createElement("a");
    const file = new Blob([robotsOutput], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = "robots.txt";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleReset = () => {
    setDefaultAccess('allow');
    setCrawlDelay('0');
    setSitemapUrl('');
    setRules([{ userAgent: '*', access: 'allow', path: '/' }]);
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%', paddingBottom: '60px' }}>
      <SEO 
        title="Free Robots.txt Generator - SEO Crawling Rules" 
        description="Create a customized robots.txt file instantly. Define specific user-agent directives, crawl delays, and XML sitemap paths for search engine spiders."
        keywords="robots txt generator, create robots txt, seo robots file, googlebot directives, disallow paths, web crawler rules"
      />
      
      {/* Breadcrumbs Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px', fontSize: '0.9rem' }}>
        <Link to="/" style={{ color: 'var(--text-secondary)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Robots.txt Generator</span>
      </div>

      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 8px 0' }}>
          Robots.txt Generator & Validator
        </h1>
        <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', margin: 0 }}>
          Generate exact crawler directives and block unauthorized AI scrapers or search engine spiders from indexing specific server folders.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '32px' }} className="tool-grid-split">
        
        {/* Left Side: Inputs */}
        <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)', alignSelf: 'start' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>Crawler Rules</h3>
            <button onClick={handleReset} style={{ background: 'rgba(15, 23, 42, 0.04)', border: 'none', padding: '6px 12px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <RotateCcw size={14} /> Clear All
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            
            {/* Sitemap URL */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>XML Sitemap URL</label>
              <input 
                type="text" 
                value={sitemapUrl} 
                onChange={(e) => setSitemapUrl(e.target.value)}
                placeholder="https://example.com/sitemap.xml"
                style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '0.95rem', outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
              />
            </div>

            {/* Crawl Delay */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>Crawl Delay (Seconds)</label>
              <select 
                value={crawlDelay} 
                onChange={(e) => setCrawlDelay(e.target.value)}
                style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '0.95rem', outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
              >
                <option value="0">Default (No delay)</option>
                <option value="1">1 Second (Fast)</option>
                <option value="5">5 Seconds (Moderate)</option>
                <option value="10">10 Seconds (Slow server throttle)</option>
              </select>
            </div>

            {/* Rules Array Header */}
            <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <span style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--text-primary)' }}>User-Agent Directives</span>
                <button 
                  onClick={addRule}
                  style={{ background: 'rgba(5, 117, 251, 0.1)', color: 'var(--primary)', border: 'none', padding: '6px 12px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  <Plus size={14} /> Add Directive
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {rules.map((rule, idx) => (
                  <div key={idx} style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: '14px', border: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '12px' }}>
                      <div>
                        <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>User-Agent Spider</span>
                        <input 
                          type="text" 
                          value={rule.userAgent} 
                          onChange={(e) => updateRule(idx, 'userAgent', e.target.value)}
                          placeholder="* or Googlebot" 
                          style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Access Directive</span>
                        <select 
                          value={rule.access} 
                          onChange={(e) => updateRule(idx, 'access', e.target.value)}
                          style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                        >
                          <option value="allow">Allow</option>
                          <option value="disallow">Disallow</option>
                        </select>
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Relative Folder Path</span>
                        <input 
                          type="text" 
                          value={rule.path} 
                          onChange={(e) => updateRule(idx, 'path', e.target.value)}
                          placeholder="/private/" 
                          style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                        />
                      </div>
                      {rules.length > 1 && (
                        <button 
                          onClick={() => removeRule(idx)} 
                          style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: 'none', width: '38px', height: '38px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', alignSelf: 'flex-end', transition: 'var(--transition-smooth)' }}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>

        {/* Right Side: Output Code + Download */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Bot size={18} style={{ color: '#10b981' }} /> Output robots.txt File
              </h3>

              <div style={{ display: 'flex', gap: '8px' }}>
                <AdToast message={toast} />
                <button 
                  onClick={withAd('robots-download', handleDownload)}
                  style={{ background: '#10b981', color: '#ffffff', border: 'none', padding: '8px 16px', borderRadius: '10px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', transition: 'var(--transition-smooth)' }}
                >
                  <Download size={16} /> Download File
                </button>
                <button 
                  onClick={withAd('robots-copy', handleCopy)}
                  style={{ background: copied ? '#10b981' : 'var(--primary)', color: '#ffffff', border: 'none', padding: '8px 16px', borderRadius: '10px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', transition: 'var(--transition-smooth)' }}
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                  {copied ? 'Copied Code!' : 'Copy Code'}
                </button>
              </div>
            </div>

            <pre style={{ background: '#0f172a', color: '#e2e8f0', padding: '20px', borderRadius: '14px', fontSize: '0.85rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', overflowX: 'auto', margin: 0, border: '1px solid rgba(255, 255, 255, 0.1)', lineHeight: 1.6 }}>
              {robotsOutput}
            </pre>
          </div>

          <div style={{ background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(5, 117, 251, 0.05) 100%)', border: '1px solid rgba(16, 185, 129, 0.2)', borderRadius: '20px', padding: '24px', display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <div style={{ background: '#10b981', color: '#ffffff', padding: '10px', borderRadius: '12px' }}>
              <ShieldCheck size={24} />
            </div>
            <div>
              <h4 style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 6px 0' }}>Deployment Recommendation</h4>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
                Save this file exactly as <strong>robots.txt</strong> and upload it to the root public folder of your domain web server (e.g., <code>https://yourdomain.com/robots.txt</code>). Search engine spiders will automatically read this file before crawling your site.
              </p>
            </div>
          </div>

        </div>

      </div>

      <style>{`
        @media (min-width: 992px) {
          .tool-grid-split { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>

      <AdUnit />
      <ToolPageExtras toolId="robots-txt-generator" />
    </div>
  );
};

export default RobotsTxtGenerator;
